/**
 * Client-side session store (Zustand).
 *
 * The raw JWT lives in an httpOnly cookie (`archon_jwt`) set by the
 * server. The browser CANNOT read that cookie from JavaScript — that
 * is by design (XSS protection). All authenticated API calls send the
 * cookie automatically via `credentials: 'include'`.
 *
 * This store therefore holds the *decoded claims* derived from the
 * server's JSON response (which contains the user record + expiry),
 * NOT a JWS-decoded token. The server is the source of truth for the
 * JWT shape — clients should never decode it themselves.
 *
 * Persists to sessionStorage so a page reload mid-flow doesn't drop
 * the user's decoded claims. Cleared on logout.
 *
 * History: AWSDO-1188 — prior versions called `decodeJwt(token)` against
 * a `token` field that the auth-api has never returned in its JSON body
 * (the JWT lives only in the httpOnly cookie). That call threw "JWTs
 * must use Compact JWS serialization, JWT must be a string" on every
 * clean-browser login and broke the post-login redirect for any session
 * that didn't already have sessionStorage primed. The fix derives
 * claims from the AuthenticatedUser + expiresIn pair the server
 * actually returns, and removes the impossible client-side JWT decode.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
/**
 * Build a JwtClaims object from the AuthenticatedUser + expiresIn pair
 * returned by /auth-api/login. The server is the source of truth — we
 * mirror what we know server-side without trying to decode the cookie.
 */
function buildClaims(user, expiresIn) {
    const nowSec = Math.floor(Date.now() / 1000);
    return {
        sub: user.id,
        username: user.username,
        email: user.email,
        organization: user.organization,
        role: user.role,
        permissions: user.permissions,
        iat: nowSec,
        exp: nowSec + expiresIn,
    };
}
export const useSessionStore = create()(persist((set, get) => ({
    user: null,
    claims: null,
    redirectUrl: null,
    setSession: (user, redirectUrl, expiresIn) => {
        const claims = buildClaims(user, expiresIn);
        set({ user, claims, redirectUrl });
    },
    clearSession: () => {
        set({ user: null, claims: null, redirectUrl: null });
    },
    isExpired: () => {
        const { claims } = get();
        if (!claims)
            return true;
        const nowSec = Math.floor(Date.now() / 1000);
        return nowSec >= claims.exp;
    },
}), {
    name: 'dymaxion-session',
    storage: createJSONStorage(() => sessionStorage),
    // Persist only UI-relevant fields. The raw JWT lives exclusively
    // in the httpOnly cookie set by the server.
    partialize: (state) => ({
        user: state.user,
        claims: state.claims,
        redirectUrl: state.redirectUrl,
    }),
}));
//# sourceMappingURL=session.js.map